<template>
    <DForm
        @submit.prevent="submitForm"
    >
        <div class="tenant-settings">
            <DCard
                :title="__('settings.tenant_settings.form.headline.general')"
                padding
            >
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.enable_email_communication.label')"
                    :is-required="true"
                    :error="errors.enable_email_communication"
                    :hint="__('settings.tenant_settings.form.fields.enable_email_communication.description')"
                >
                    <DSwitch
                        v-model="form.enable_email_communication"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.sms_communications_label')"
                    :is-required="true"
                    :error="errors.sms_communications"
                    :hint="__('settings.tenant_settings.form.fields.sms_communications_hint')"
                >
                    <DSwitch
                        v-model="form.sms_communications"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.sms_send_from_time_label')"
                    :error="errors.sms_send_from_time"
                    :hint="__('settings.tenant_settings.form.fields.sms_send_from_time_label_hint')"
                >
                    <DInput
                        id="sms_send_from_time"
                        v-model="form.sms_send_from_time"
                        type="time"
                        :is-valid="isFormFieldValid('sms_send_from_time')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.sms_send_to_time_label')"
                    :error="errors.sms_send_to_time"
                    :hint="__('settings.tenant_settings.form.fields.sms_send_to_time_label_hint')"
                >
                    <DInput
                        id="sms_send_to_time"
                        v-model="form.sms_send_to_time"
                        type="time"
                        :is-valid="isFormFieldValid('sms_send_to_time')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.internal_support_url.label')"
                    :error="errors.internal_support_url"
                    :hint="__('settings.tenant_settings.form.fields.internal_support_url.description')"
                >
                    <DInput
                        id="internal_support_url"
                        v-model="form.internal_support_url"
                        :is-valid="isFormFieldValid('internal_support_url')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.currency_code_label')"
                    :is-required="true"
                    :error="errors.currency_code"
                    :hint="__('settings.tenant_settings.form.fields.currency_code_hint')"
                >
                    <p
                        v-if="currencyWarningMessage"
                        class="alert alert-warning info-message mb-3"
                    >
                        <span>{{ __('settings.tenant_settings.form.fields.currency_warning_line_1') }}</span><br>
                        <span>{{ __('settings.tenant_settings.form.fields.currency_warning_line_2') }}</span><br>
                        <span>{{ __('settings.tenant_settings.form.fields.currency_warning_line_3') }}</span><br>
                        <span>{{ __('settings.tenant_settings.form.fields.currency_warning_line_4') }}</span>
                    </p>
                    <DSelect
                        id="currency_code"
                        v-model="form.currency_code"
                        :options="currencyCode"
                        :is-valid="isFormFieldValid('currency_code')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.currency_symbol_label')"
                    :error="errors.currency_symbol"
                    :hint="__('settings.tenant_settings.form.fields.currency_symbol_label_hint')"
                >
                    <DSelect
                        id="currency_symbol"
                        v-model="form.currency_symbol"
                        :options="currencySymbol"
                        :is-valid="isFormFieldValid('currency_symbol')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.trainings_course_link')"
                    :error="errors.trainings_course_link"
                    :hint="__('settings.tenant_settings.form.fields.trainings_course_link_hint')"
                >
                    <DInput
                        id="trainings_course_link"
                        v-model="form.trainings_course_link"
                        :is-valid="isFormFieldValid('trainings_course_link')"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('settings.tenant_settings.form.headline.contracts')"
                padding
            >
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.short_termination_period.label')"
                    :error="errors.short_termination_period"
                    :hint="__('settings.tenant_settings.form.fields.short_termination_period.description')"
                >
                    <DFormCheck
                        id="short_termination_period"
                        v-model="form.short_termination_period"
                        inline
                        :is-valid="isFormFieldValid('short_termination_period')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.quickstart.label')"
                    :error="errors.quickstart"
                    :hint="__('settings.tenant_settings.form.fields.quickstart.description')"
                >
                    <DFormCheck
                        id="quickstart"
                        v-model="form.quickstart"
                        inline
                        :is-valid="isFormFieldValid('quickstart')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.automatic_price.label')"
                    :error="errors.automatic_price"
                    :hint="__('settings.tenant_settings.form.fields.automatic_price.description')"
                >
                    <DFormCheck
                        id="automatic_price"
                        v-model="form.automatic_price"
                        inline
                        :is-valid="isFormFieldValid('automatic_price')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.automatic_price === true"
                    :label="__('settings.tenant_settings.form.fields.automatic_price_number_of_months.label')"
                    :is-required="true"
                    :error="errors.automatic_price_number_of_months"
                    :hint="__('settings.tenant_settings.form.fields.automatic_price_number_of_months.description')"
                >
                    <DInput
                        id="automatic_price_number_of_months"
                        v-model="form.automatic_price_number_of_months"
                        :is-valid="isFormFieldValid('automatic_price_number_of_months')"
                        type="number"
                        max="360"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.allow_contract_termination')"
                    :error="errors.allow_contract_termination"
                    :hint="__('settings.tenant_settings.form.fields.allow_contract_termination_hint')"
                >
                    <DFormCheck
                        id="quickstart"
                        v-model="form.allow_contract_termination"
                        inline
                        :is-valid="isFormFieldValid('allow_contract_termination')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.enable_delivery_notes.label')"
                    :error="errors.enable_delivery_notes"
                    :hint="__('settings.tenant_settings.form.fields.enable_delivery_notes.description')"
                >
                    <DSwitch
                        v-model="form.enable_delivery_notes"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.enable_divergent_payment_methods.label')"
                    :error="errors.enable_divergent_payment_methods"
                    :hint="__('settings.tenant_settings.form.fields.enable_divergent_payment_methods.description')"
                >
                    <DSwitch
                        v-model="form.enable_divergent_payment_methods"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.enable_prompt_cash_discount.label')"
                    :error="errors.enable_prompt_cash_discount"
                    :hint="__('settings.tenant_settings.form.fields.enable_prompt_cash_discount.description')"
                >
                    <DSwitch
                        v-model="form.enable_prompt_cash_discount"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.send_renewal_mail_label')"
                    :is-required="true"
                    :error="errors.send_renewal_mail"
                    :hint="__('settings.tenant_settings.form.fields.send_renewal_mail_hint')"
                >
                    <DSwitch
                        v-model="form.send_renewal_mail"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.send_renewal_mail === true"
                    :label="__('settings.tenant_settings.form.fields.renewal_mail_days')"
                    :is-required="true"
                    :error="errors.renewal_mail_days"
                    :hint="__('settings.tenant_settings.form.fields.renewal_mail_days_hint')"
                >
                    <DInput
                        id="renewal_mail_days"
                        v-model="form.renewal_mail_days"
                        :placeholder="__('settings.tenant_settings.form.fields.renewal_mail_days')"
                        :is-valid="isFormFieldValid('renewal_mail_days')"
                        type="number"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.send_renewal_mail === true"
                    :label="__('settings.tenant_settings.form.fields.renewal_minimum_contract_days')"
                    :is-required="true"
                    :error="errors.renewal_minimum_contract_days"
                    :hint="__('settings.tenant_settings.form.fields.renewal_minimum_contract_days_hint')"
                >
                    <DInput
                        id="renewal_minimum_contract_days"
                        v-model="form.renewal_minimum_contract_days"
                        :placeholder="__('settings.tenant_settings.form.fields.renewal_minimum_contract_days')"
                        :is-valid="isFormFieldValid('renewal_minimum_contract_days')"
                        type="number"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.send_renewal_mail === true"
                    :label="__('settings.tenant_settings.form.fields.renewal_minimum_contract_months')"
                    :is-required="true"
                    :error="errors.renewal_minimum_contract_months"
                    :hint="__('settings.tenant_settings.form.fields.renewal_minimum_contract_months_hint')"
                >
                    <DInput
                        id="renewal_minimum_contract_months"
                        v-model="form.renewal_minimum_contract_months"
                        :placeholder="__('settings.tenant_settings.form.fields.renewal_minimum_contract_months')"
                        :is-valid="isFormFieldValid('renewal_minimum_contract_months')"
                        type="number"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.generic_termination_notification.label')"
                    :is-required="true"
                    :error="errors.generic_termination_notification"
                    :hint="__('settings.tenant_settings.form.fields.generic_termination_notification.description')"
                >
                    <DSwitch
                        v-model="form.generic_termination_notification"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.generic_termination_notification === true"
                    :label="__('settings.tenant_settings.form.fields.generic_termination_notification_days')"
                    :is-required="form.generic_termination_notification === true"
                    :error="errors.generic_termination_notification_days"
                    :hint="__('settings.tenant_settings.form.fields.generic_termination_notification_days_hint')"
                >
                    <DInput
                        id="generic_termination_notification_days"
                        v-model="form.generic_termination_notification_days"
                        :placeholder="__('settings.tenant_settings.form.fields.generic_termination_notification_days')"
                        :is-valid="isFormFieldValid('generic_termination_notification_days')"
                        type="number"
                        min="2"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.generic_second_termination_notification.label')"
                    :is-required="true"
                    :error="errors.generic_second_termination_notification"
                    :hint="__('settings.tenant_settings.form.fields.generic_second_termination_notification.description')"
                >
                    <DSwitch
                        v-model="form.generic_second_termination_notification"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.generic_second_termination_notification === true && form.generic_second_termination_notification === true"
                    :label="__('settings.tenant_settings.form.fields.generic_second_termination_notification_days')"
                    :is-required="form.generic_second_termination_notification === true"
                    :error="errors.generic_second_termination_notification_days"
                    :hint="__('settings.tenant_settings.form.fields.generic_second_termination_notification_days_hint')"
                >
                    <DInput
                        id="generic_termination_notification_days"
                        v-model="form.generic_second_termination_notification_days"
                        :placeholder="__('settings.tenant_settings.form.fields.generic_second_termination_notification_days')"
                        :is-valid="isFormFieldValid('generic_second_termination_notification_days')"
                        type="number"
                        min="2"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.supports_installments.label')"
                    :is-required="true"
                    :error="errors.supports_installments"
                    :hint="__('settings.tenant_settings.form.fields.supports_installments.description')"
                >
                    <DSwitch
                        v-model="form.supports_installments"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.approve_paid_ownerchanges.label')"
                    :is-required="true"
                    :error="errors.approve_paid_ownerchanges"
                    :hint="__('settings.tenant_settings.form.fields.approve_paid_ownerchanges.description')"
                >
                    <DSwitch
                        v-model="form.approve_paid_ownerchanges"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.use_last_paid_period_as_termination_bottom_threshold')"
                    :is-required="true"
                    :error="errors.use_last_paid_period_as_termination_bottom_threshold"
                    :hint="__('settings.tenant_settings.form.fields.use_last_paid_period_as_termination_bottom_threshold_hint')"
                >
                    <DSwitch
                        v-model="form.use_last_paid_period_as_termination_bottom_threshold"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.reset_billed_until_date_to_last_paid_period')"
                    :is-required="true"
                    :error="errors.reset_billed_until_date_to_last_paid_period"
                    :hint="__('settings.tenant_settings.form.fields.reset_billed_until_date_to_last_paid_period_hint')"
                >
                    <DSwitch
                        v-model="form.reset_billed_until_date_to_last_paid_period"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.cancel_invoice_items_when_terminating_the_contract')"
                    :is-required="true"
                    :error="errors.cancel_invoice_items_when_terminating_the_contract"
                    :hint="__('settings.tenant_settings.form.fields.cancel_invoice_items_when_terminating_the_contract_hint')"
                >
                    <DSwitch
                        v-model="form.cancel_invoice_items_when_terminating_the_contract"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('settings.tenant_settings.form.headline.payments')"
                padding
            >
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.add_payment_link')"
                    :is-required="true"
                    :error="errors.add_payment_link"
                    :hint="__('settings.tenant_settings.form.fields.payment_link_description')"
                >
                    <DFormCheck
                        id="add_payment_link"
                        v-model="form.add_payment_link"
                        inline
                        :is-valid="isFormFieldValid('add_payment_link')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.add_payment_link"
                    :label="__('settings.tenant_settings.form.fields.payment_link')"
                    :is-required="true"
                    :error="errors.payment_link"
                    :hint="__('settings.tenant_settings.form.fields.payment_link_hint')"
                >
                    <DInput
                        id="payment_link"
                        v-model="form.payment_link"
                        :placeholder="__('settings.tenant_settings.form.fields.payment_link')"
                        :is-valid="isFormFieldValid('payment_link')"
                        maxlength="255"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.premature_automatic_payment_capture_days')"
                    :is-required="true"
                    :error="errors.premature_automatic_payment_capture_days"
                    :hint="__('settings.tenant_settings.form.fields.premature_automatic_payment_capture_days_description')"
                >
                    <DInput
                        id="premature_automatic_payment_capture_days"
                        v-model="form.premature_automatic_payment_capture_days"
                        :placeholder="__('settings.tenant_settings.form.fields.premature_automatic_payment_capture_days')"
                        :is-valid="isFormFieldValid('premature_automatic_payment_capture_days')"
                        type="number"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.sepa_direct_debit_pain_version.label')"
                    :is-required="true"
                    :error="errors.sepa_direct_debit_pain_version"
                    :hint="__('settings.tenant_settings.form.fields.sepa_direct_debit_pain_version.hint')"
                >
                    <DSelect
                        id="sepa_direct_debit_pain_version"
                        v-model="form.sepa_direct_debit_pain_version"
                        :options="sepaDirectDebitPainVersions"
                        :placeholder="__('settings.tenant_settings.form.fields.sepa_direct_debit_pain_version.label')"
                        :is-valid="isFormFieldValid('sepa_direct_debit_pain_version')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.send_payment_confirmation_email')"
                    :is-required="true"
                    :error="errors.send_payment_confirmation_email"
                    :hint="__('settings.tenant_settings.form.fields.send_payment_confirmation_email_description')"
                >
                    <DSwitch
                        v-model="form.send_payment_confirmation_email"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('settings.tenant_settings.form.headline.invoicing')"
                padding
            >
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.vat_id_check_type.label')"
                    :is-required="true"
                    :error="errors.vat_id_check_type"
                    :hint="__('settings.tenant_settings.form.fields.vat_id_check_type.label_hint')"
                >
                    <DSelect
                        id="vat_id_check_type"
                        v-model="form.vat_id_check_type"
                        :options="vatIdCheckTypeOptions"
                        :placeholder="__('messages.select_value')"
                        :is-valid="isFormFieldValid('vat_id_check_type')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.lead_day_for_invoice_label')"
                    :is-required="true"
                    :error="errors.lead_day_for_invoice"
                    :hint="__('settings.tenant_settings.form.fields.lead_day_for_invoice_description')"
                >
                    <DInput
                        id="lead_day_for_invoice"
                        v-model="form.lead_day_for_invoice"
                        :placeholder="__('settings.tenant_settings.form.fields.lead_day_for_invoice_label')"
                        :is-valid="isFormFieldValid('lead_day_for_invoice')"
                        type="number"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.duplicate_watermark.label')"
                    :error="errors.duplicate_watermark"
                    :hint="__('settings.tenant_settings.form.fields.duplicate_watermark.description')"
                >
                    <DFormCheck
                        id="duplicate_watermark"
                        v-model="form.duplicate_watermark"
                        inline
                        :is-valid="isFormFieldValid('duplicate_watermark')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.creditworthiness_check.label')"
                    :error="errors.creditworthiness_check"
                    :hint="__('settings.tenant_settings.form.fields.creditworthiness_check.description')"
                >
                    <DFormCheck
                        id="creditworthiness_check"
                        v-model="form.creditworthiness_check"
                        inline
                        :is-valid="isFormFieldValid('creditworthiness_check')"
                        @click="creditworthinessUpdated"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.creditworthiness_check === true"
                    :label="__('settings.tenant_settings.form.fields.creditworthiness_incident_type_id.label')"
                    :error="errors.creditworthiness_incident_type_id"
                    :hint="__('settings.tenant_settings.form.fields.creditworthiness_incident_type_id.description')"
                >
                    <DSelect
                        id="creditworthiness_incident_type_id"
                        v-model="form.creditworthiness_incident_type_id"
                        :options="businessIncidentTypes"
                        :placeholder="__('messages.select_value')"
                        :is-valid="isFormFieldValid('creditworthiness_incident_type_id')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.online_service_fee.label')"
                    :error="errors.online_service_fee"
                    :hint="__('settings.tenant_settings.form.fields.online_service_fee.description')"
                >
                    <DFormCheck
                        id="online_service_fee_check"
                        v-model="form.online_service_fee"
                        inline
                        :is-valid="isFormFieldValid('online_service_fee')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.diamant_settings.form.credit_booking_enabled')"
                    :is-required="true"
                    :error="errors.credit_booking_enabled"
                    :hint="__('settings.diamant_settings.form.credit_booking_enabled_hint')"
                >
                    <DSwitch
                        v-model="form.credit_booking_enabled"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.send_invoice_only_to_one_address.label')"
                    :error="errors.send_invoice_only_to_one_address"
                    :hint="__('settings.tenant_settings.form.fields.send_invoice_only_to_one_address.description')"
                >
                    <DSwitch
                        v-model="form.send_invoice_only_to_one_address"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.electronic_invoice_enabled')"
                    :error="errors.electronic_invoice_enabled"
                    :hint="__('settings.tenant_settings.form.fields.electronic_invoice_enabled_hint')"
                >
                    <DSwitch
                        v-model="form.electronic_invoice_enabled"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.electronic_invoice_enabled"
                    :label="__('settings.tenant_settings.form.fields.electronic_invoice_provider')"
                    :is-required="false"
                    :error="errors.electronic_invoice_provider"
                    :hint="__('settings.tenant_settings.form.fields.electronic_invoice_provider_hint')"
                >
                    <DSelect
                        id="electronic_invoice_provider"
                        v-model="form.electronic_invoice_provider"
                        :options="electronicInvoiceProviders"
                        :placeholder="__('settings.tenant_settings.form.fields.electronic_invoice_provider')"
                        :is-valid="isFormFieldValid('electronic_invoice_provider')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.electronic_invoice_enabled && ['payex360', 'pagero'].includes(form.electronic_invoice_provider)"
                    :label="__('settings.tenant_settings.form.fields.electronic_invoicing_types')"
                    :is-required="true"
                    :error="errors.electronic_invoicing_types"
                    :hint="__('settings.tenant_settings.form.fields.electronic_invoicing_types_hint')"
                >
                    <DMultiSelect
                        id="electronic_invoicing_types"
                        :options="setElectronicInvoicingTypes(form.electronic_invoicing_types)"
                        :selected="form.electronic_invoicing_types"
                        :selection="true"
                        selection-type="tags"
                        :search="true"
                        @update="updateSelectedElectronicInvoicingTypes"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.electronic_invoice_enabled && form.electronic_invoice_provider === 'tietoevry'"
                    :label="__('settings.tenant_settings.form.fields.electronic_invoicing_operators')"
                    :is-required="true"
                    :error="errors.electronic_invoicing_operators"
                    :hint="__('settings.tenant_settings.form.fields.electronic_invoicing_operators_hint')"
                >
                    <DMultiSelect
                        id="electronic_invoicing_operators"
                        :options="electronicInvoiceOperators"
                        :selected="form.electronic_invoicing_operators"
                        :selection="true"
                        selection-type="tags"
                        :search="true"
                        @update="updateSelectedElectronicInvoicingOperators"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.invoice_before_renewal.label')"
                    :error="errors.invoice_before_renewal"
                    :hint="__('settings.tenant_settings.form.fields.invoice_before_renewal.description')"
                >
                    <DSwitch
                        v-model="form.invoice_before_renewal"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.invoice_before_renewal"
                    :label="__('settings.tenant_settings.form.fields.invoice_before_renewal_days')"
                    :is-required="form.invoice_before_renewal === true"
                    :error="errors.invoice_before_renewal_days"
                    :hint="__('settings.tenant_settings.form.fields.invoice_before_renewal_days_hint')"
                >
                    <DInput
                        id="invoice_before_renewal_days"
                        v-model="form.invoice_before_renewal_days"
                        type="number"
                        :placeholder="__('settings.tenant_settings.form.fields.invoice_before_renewal_days')"
                        :is-valid="isFormFieldValid('invoice_before_renewal_days')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="form.credit_booking_enabled === true"
                    :label="__('settings.tenant_settings.form.fields.email_address_accounting_department')"
                    :is-required="true"
                    :error="errors.email_address_accounting_department"
                    :hint="__('settings.tenant_settings.form.fields.email_address_accounting_department_hint')"
                >
                    <DInput
                        id="email_address_accounting_department"
                        v-model="form.email_address_accounting_department"
                        type="email"
                        :placeholder="__('settings.tenant_settings.form.fields.email_address_accounting_department')"
                        :is-valid="isFormFieldValid('email_address_accounting_department')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.always_resend_invoice_per_email')"
                    :is-required="true"
                    :error="errors.always_resend_invoice_per_email"
                    :hint="__('settings.tenant_settings.form.fields.always_resend_invoice_per_email_hint')"
                >
                    <DSwitch
                        v-model="form.always_resend_invoice_per_email"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
            <DFormControls
                :dirty="form.isDirty"
                @submit="submitForm"
            />
            <DCard
                :title="__('settings.tenant_settings.form.headline.customer')"
                padding
            >
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.salutation_mandatory')"
                    :hint="__('settings.tenant_settings.form.fields.salutation_mandatory_hint')"
                    :is-required="true"
                    :error="errors.salutation_mandatory"
                >
                    <DSwitch
                        id="salutation_mandatory"
                        v-model="form.salutation_mandatory"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.use_herold_location_id_on_customer.label')"
                    :is-required="true"
                    :error="errors.use_herold_location_id_on_customer"
                    :hint="__('settings.tenant_settings.form.fields.use_herold_location_id_on_customer.description')"
                >
                    <DSwitch
                        v-model="form.use_herold_location_id_on_customer"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.ssn_business_id')"
                    :is-required="true"
                    :error="errors.ssn_business_id"
                    :hint="__('settings.tenant_settings.form.fields.ssn_business_id_hint')"
                >
                    <DSwitch
                        v-model="form.ssn_business_id"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.enable_commercial_register_number')"
                    :error="errors.enable_commercial_register_number"
                    :hint="__('settings.tenant_settings.form.fields.enable_commercial_register_number_hint')"
                >
                    <DSwitch
                        v-model="form.enable_commercial_register_number"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.norway_id')"
                    :is-required="false"
                    :error="errors.norway_id"
                    :hint="__('settings.tenant_settings.form.fields.norway_id_hint')"
                >
                    <DSwitch
                        id="norway_id"
                        v-model="form.norway_id"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.send_changed_communications_mail.label')"
                    :is-required="false"
                    :error="errors.send_changed_communications_mail"
                    :hint="__('settings.tenant_settings.form.fields.send_changed_communications_mail.description')"
                >
                    <DSwitch
                        id="send_changed_communications_mail"
                        v-model="form.send_changed_communications_mail"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.allow_cloudpit_control_panel_switch')"
                    :error="errors.allow_cloudpit_control_panel_switch"
                    :hint="__('settings.tenant_settings.form.fields.allow_cloudpit_control_panel_switch_hint')"
                >
                    <DSwitch
                        v-model="form.allow_cloudpit_control_panel_switch"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.use_customer_reference_number.label')"
                    :error="errors.use_customer_reference_number"
                    :hint="__('settings.tenant_settings.form.fields.use_customer_reference_number.description')"
                >
                    <DSwitch
                        id="use_customer_reference_number"
                        v-model="form.use_customer_reference_number"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('settings.tenant_settings.form.headline.email_verification')"
                padding
            >
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.domain_email_verification.domain_email_verification_on_domain_fulfillment.label')"
                    :is-required="false"
                    :error="errors.domain_email_verification_on_domain_fulfillment"
                    :hint="__('settings.tenant_settings.form.fields.domain_email_verification.domain_email_verification_on_domain_fulfillment.label_hint')"
                >
                    <DSwitch
                        id="domain_email_verification_on_domain_fulfillment"
                        v-model="form.domain_email_verification_on_domain_fulfillment"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.domain_email_verification.domain_email_verification_on_domain_owner_change.label')"
                    :is-required="false"
                    :error="errors.domain_email_verification_on_domain_owner_change"
                    :hint="__('settings.tenant_settings.form.fields.domain_email_verification.domain_email_verification_on_domain_owner_change.label_hint')"
                >
                    <DSwitch
                        id="domain_email_verification_on_domain_owner_change"
                        v-model="form.domain_email_verification_on_domain_owner_change"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.domain_email_verification.manual_domain_email_verification.label')"
                    :is-required="false"
                    :error="errors.manual_domain_email_verification"
                    :hint="__('settings.tenant_settings.form.fields.domain_email_verification.manual_domain_email_verification.label_hint')"
                >
                    <DSwitch
                        id="manual_domain_email_verification"
                        v-model="form.manual_domain_email_verification"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('settings.tenant_settings.form.headline.fulfillment')"
                padding
            >
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.prevent_defulfillment_of_duplicate_parent.label')"
                    :is-required="true"
                    :error="errors.prevent_defulfillment_of_duplicate_parent"
                    :hint="__('settings.tenant_settings.form.fields.prevent_defulfillment_of_duplicate_parent.description')"
                >
                    <DSwitch
                        v-model="form.prevent_defulfillment_of_duplicate_parent"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.uses_partner_api.label')"
                    :is-required="true"
                    :error="errors.uses_partner_api"
                    :hint="__('settings.tenant_settings.form.fields.uses_partner_api.description')"
                >
                    <DSwitch
                        v-model="form.uses_partner_api"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.partner_callback_url.label')"
                    :error="errors.partner_callback_url"
                    :hint="__('settings.tenant_settings.form.fields.partner_callback_url.hint')"
                >
                    <DInput
                        id="partner_callback_url"
                        v-model="form.partner_callback_url"
                        :is-valid="isFormFieldValid('partner_callback_url')"
                        :placeholder="__('settings.tenant_settings.form.fields.partner_callback_url.description')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.send_managed_vps_welcome_email_using_messenger.label')"
                    :is-required="true"
                    :error="errors.send_managed_vps_welcome_email_using_messenger"
                    :hint="__('settings.tenant_settings.form.fields.send_managed_vps_welcome_email_using_messenger.description')"
                >
                    <DSwitch
                        v-model="form.send_managed_vps_welcome_email_using_messenger"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('settings.tenant_settings.form.headline.gdpr')"
                :subtitle="__('settings.tenant_settings.form.fields.gdpr.hint')"
                padding
            >
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.gdpr.gdpr_inactive_months.label')"
                    :is-required="true"
                    :error="errors.gdpr_inactive_months"
                    :hint="__('settings.tenant_settings.form.fields.gdpr.gdpr_inactive_months.hint')"
                >
                    <DInput
                        id="gdpr_inactive_months"
                        v-model="form.gdpr_inactive_months"
                        :is-valid="isFormFieldValid('gdpr_inactive_months')"
                        type="number"
                        min="0"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.gdpr.gdpr_restrict_years.label')"
                    :is-required="true"
                    :error="errors.gdpr_restrict_years"
                    :hint="__('settings.tenant_settings.form.fields.gdpr.gdpr_restrict_years.hint')"
                >
                    <DInput
                        id="gdpr_restrict_years"
                        v-model="form.gdpr_restrict_years"
                        :is-valid="isFormFieldValid('gdpr_restrict_years')"
                        type="number"
                        min="0"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.gdpr.gdpr_delete_years.label')"
                    :is-required="true"
                    :error="errors.gdpr_delete_years"
                    :hint="__('settings.tenant_settings.form.fields.gdpr.gdpr_delete_years.hint')"
                >
                    <DInput
                        id="gdpr_delete_years"
                        v-model="form.gdpr_delete_years"
                        :is-valid="isFormFieldValid('gdpr_delete_years')"
                        type="number"
                        min="0"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.gdpr.gdpr_min_restricted_years.label')"
                    :is-required="true"
                    :error="errors.gdpr_min_restricted_years"
                    :hint="__('settings.tenant_settings.form.fields.gdpr.gdpr_min_restricted_years.hint')"
                >
                    <DInput
                        id="gdpr_min_restricted_years"
                        v-model="form.gdpr_min_restricted_years"
                        :is-valid="isFormFieldValid('gdpr_min_restricted_years')"
                        type="number"
                        min="0"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.gdpr.gdpr_incident_type.label')"
                    :error="errors.gdpr_incident_type"
                    :hint="__('settings.tenant_settings.form.fields.gdpr.gdpr_incident_type.label_hint')"
                >
                    <DSelect
                        id="gdpr_incident_type"
                        v-model="form.gdpr_incident_type"
                        :options="businessIncidentTypes"
                        :placeholder="__('settings.tenant_settings.form.fields.gdpr.gdpr_incident_type.label')"
                        :is-valid="isFormFieldValid('gdpr_incident_type')"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('settings.tenant_settings.form.headline.php_extended_support')"
                padding
            >
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.enable_php_eol_temporal_workflow.label')"
                    :is-required="true"
                    :error="errors.enable_php_eol_temporal_workflow"
                    :hint="__('settings.tenant_settings.form.fields.enable_php_eol_temporal_workflow.description')"
                >
                    <DSwitch
                        v-model="form.enable_php_eol_temporal_workflow"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.tenant_settings.form.fields.php_eol_excluded_payment_methods.label')"
                    :is-required="false"
                    :error="errors.php_eol_excluded_payment_methods"
                    :hint="__('settings.tenant_settings.form.fields.php_eol_excluded_payment_methods.description')"
                >
                    <DMultiSelect
                        id="php_eol_excluded_payment_methods"
                        :options="setAvailablePaymentMethodOptions(form.php_eol_excluded_payment_methods)"
                        :selected="form.php_eol_excluded_payment_methods"
                        :selection="true"
                        selection-type="tags"
                        :search="true"
                        @update="updateSelectedPhpEolExcludedPaymentMethods"
                    />
                </DFormRow>
            </DCard>
        </div>
    </DForm>
</template>

<script>
export default {
    name: 'TenantSettingsForm',
    props: {
        tenantSetting: {
            type: Object,
            default: () => { return {} }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        vatIdCheckTypes: {
            type: Array,
            default: () => { return [] }
        },
        leadDayForInvoiceVatIdCheckDefault: {
            type: Number,
            default: () => { return 14 }
        },
        vatIdCheckTypeDefault: {
            type: String,
            default: () => { return 'do_not_check' }
        },
        electronicInvoiceProviders: {
            type: Array,
            default: () => { return [] }
        },
        electronicInvoicingTypesPagero: {
            type: Array,
            default: () => { return [] }
        },
        electronicInvoicingTypesPayex360: {
            type: Array,
            default: () => { return [] }
        },
        sepaDirectDebitPainVersions: {
            type: Array,
            default: () => { return [] }
        },
        businessIncidentTypes: {
            type: Array,
            default: () => { return [] }
        },
        currencyCode: {
            type: Array,
            default: () => { return [] }
        },
        currencySymbol: {
            type: Array,
            default: () => { return [] }
        },
        electronicInvoiceOperators: {
            type: Array,
            default: () => { return [] }
        },
        availablePaymentMethods: {
            type: Array,
            default: () => { return [] }
        },
    },
    emits: ['form-submitted'],
    data () {
        return {
            form: this.$inertia.form({
                salutation_mandatory: this.tenantSetting?.salutation_mandatory ?? true,
                sms_communications: this.tenantSetting?.sms_communications ?? null,
                short_termination_period: this.tenantSetting?.short_termination_period ?? null,
                add_payment_link: this.tenantSetting?.add_payment_link ?? false,
                payment_link: this.tenantSetting?.payment_link ?? null,
                premature_automatic_payment_capture_days: this.tenantSetting?.premature_automatic_payment_capture_days ?? 90,
                sepa_direct_debit_pain_version: this.tenantSetting?.sepa_direct_debit_pain_version ?? 800302,
                send_payment_confirmation_email: this.tenantSetting?.send_payment_confirmation_email ?? false,
                vat_id_check_type: this.tenantSetting?.vat_id_check_type ?? this.vatIdCheckTypeDefault,
                lead_day_for_invoice: this.tenantSetting?.lead_day_for_invoice ?? this.leadDayForInvoiceVatIdCheckDefault,
                duplicate_watermark: this.tenantSetting?.duplicate_watermark ?? null,
                quickstart: this.tenantSetting?.quickstart ?? null,
                automatic_price: this.tenantSetting?.automatic_price ?? null,
                automatic_price_number_of_months: this.tenantSetting?.automatic_price_number_of_months ?? 24,
                creditworthiness_check: this.tenantSetting?.creditworthiness_check ?? null,
                creditworthiness_incident_type_id: this.tenantSetting?.creditworthiness_incident_type_id ?? null,
                online_service_fee: this.tenantSetting?.online_service_fee ?? null,
                credit_booking_enabled: this.tenantSetting?.credit_booking_enabled ?? true,
                send_invoice_only_to_one_address: this.tenantSetting?.send_invoice_only_to_one_address ?? true,
                allow_contract_termination: this.tenantSetting?.allow_contract_termination ?? true,
                enable_delivery_notes: this.tenantSetting?.enable_delivery_notes ?? false,
                use_herold_location_id_on_customer: this.tenantSetting?.use_herold_location_id_on_customer ?? false,
                ssn_business_id: this.tenantSetting?.ssn_business_id ?? false,
                enable_divergent_payment_methods: this.tenantSetting?.enable_divergent_payment_methods ?? false,
                enable_commercial_register_number: this.tenantSetting?.enable_commercial_register_number ?? false,
                enable_prompt_cash_discount: this.tenantSetting?.enable_prompt_cash_discount ?? false,
                electronic_invoice_enabled: this.tenantSetting?.electronic_invoice_enabled ?? false,
                electronic_invoice_provider: this.tenantSetting?.electronic_invoice_provider ?? null,
                electronic_invoicing_types: this.tenantSetting?.electronic_invoicing_types ?? [],
                send_renewal_mail: this.tenantSetting?.send_renewal_mail ?? false,
                renewal_mail_days: this.tenantSetting?.renewal_mail_days ?? null,
                renewal_minimum_contract_days: this.tenantSetting?.renewal_minimum_contract_days ?? 0,
                renewal_minimum_contract_months: this.tenantSetting?.renewal_minimum_contract_months ?? 0,
                invoice_before_renewal: this.tenantSetting?.invoice_before_renewal ?? false,
                invoice_before_renewal_days: this.tenantSetting?.invoice_before_renewal_days ?? 0,
                generic_termination_notification: this.tenantSetting?.generic_termination_notification ?? false,
                generic_termination_notification_days: this.tenantSetting?.generic_termination_notification_days ?? 2,
                generic_second_termination_notification: this.tenantSetting?.generic_second_termination_notification ?? false,
                generic_second_termination_notification_days: this.tenantSetting?.generic_second_termination_notification_days ?? 2,
                supports_installments: this.tenantSetting?.supports_installments ?? false,
                email_address_accounting_department: this.tenantSetting?.email_address_accounting_department ?? null,
                prevent_defulfillment_of_duplicate_parent: this.tenantSetting?.prevent_defulfillment_of_duplicate_parent ?? false,
                internal_support_url: this.tenantSetting?.internal_support_url ?? null,
                currency_code: this.tenantSetting?.currency_code ?? 'EUR',
                currency_symbol: this.tenantSetting?.currency_symbol ?? null,
                norway_id: this.tenantSetting?.norway_id ?? false,
                trainings_course_link: this.tenantSetting?.trainings_course_link ?? null,
                approve_paid_ownerchanges: this.tenantSetting?.approve_paid_ownerchanges ?? false,
                enable_email_communication: this.tenantSetting?.enable_email_communication ?? false,
                sms_send_from_time: this.tenantSetting?.sms_send_from_time ?? '10:00:00',
                sms_send_to_time: this.tenantSetting?.sms_send_to_time ?? '16:00:00',
                always_resend_invoice_per_email: this.tenantSetting?.always_resend_invoice_per_email ?? false,
                send_changed_communications_mail: this.tenantSetting?.send_changed_communications_mail ?? false,
                domain_email_verification_on_domain_fulfillment: this.tenantSetting?.domain_email_verification_on_domain_fulfillment ?? false,
                domain_email_verification_on_domain_owner_change: this.tenantSetting?.domain_email_verification_on_domain_owner_change ?? false,
                manual_domain_email_verification: this.tenantSetting?.manual_domain_email_verification ?? false,
                gdpr_inactive_months: this.tenantSetting?.gdpr_inactive_months ?? 6,
                gdpr_restrict_years: this.tenantSetting?.gdpr_restrict_years ?? 3,
                gdpr_delete_years: this.tenantSetting?.gdpr_delete_years ?? 10,
                gdpr_min_restricted_years: this.tenantSetting?.gdpr_min_restricted_years ?? 3,
                gdpr_incident_type: this.tenantSetting?.gdpr_incident_type ?? null,
                allow_cloudpit_control_panel_switch: this.tenantSetting?.allow_cloudpit_control_panel_switch ?? false,
                use_customer_reference_number: this.tenantSetting?.use_customer_reference_number ?? false,
                electronic_invoicing_operators: this.electronicInvoiceOperators.filter(operator => operator.selected === true).map(operator => operator.value),
                enable_php_eol_temporal_workflow: this.tenantSetting?.enable_php_eol_temporal_workflow ?? false,
                php_eol_excluded_payment_methods: this.tenantSetting?.php_eol_excluded_payment_methods ?? [],
                uses_partner_api: this.tenantSetting?.uses_partner_api ?? false,
                partner_callback_url: this.tenantSetting?.partner_callback_url ?? '',
                send_managed_vps_welcome_email_using_messenger: this.tenantSetting?.send_managed_vps_welcome_email_using_messenger ?? false,
                use_last_paid_period_as_termination_bottom_threshold: this.tenantSetting?.use_last_paid_period_as_termination_bottom_threshold ?? false,
                reset_billed_until_date_to_last_paid_period: this.tenantSetting?.reset_billed_until_date_to_last_paid_period ?? false,
                cancel_invoice_items_when_terminating_the_contract: this.tenantSetting?.cancel_invoice_items_when_terminating_the_contract ?? false
            })
        }
    },
    computed: {
        vatIdCheckTypeOptions () {
            const types = this.vatIdCheckTypes

            for (const index in types) {
                if (types[index].label.length !== 0) {
                    types[index].label = this.__(`settings.tenant_settings.form.fields.vat_id_check_type.options.${types[index].value}`)
                }
            }

            return types
        },
        currencyWarningMessage () {
            return this.form.currency_code !== 'EUR'
        },
        possibleElectronicInvoicingTypes () {
            if (this.form.electronic_invoice_provider === 'pagero') {
                return this.electronicInvoicingTypesPagero
            }

            if (this.form.electronic_invoice_provider === 'payex360') {
                return this.electronicInvoicingTypesPayex360
            }

            return []
        },
    },
    watch: {
        'form.electronic_invoice_enabled': function (newValue, oldValue) {
            if (newValue === false) {
                this.form.electronic_invoicing_types = null
                this.form.electronic_invoice_provider = null
            }
        }
    },
    methods: {
        submitForm () {
            this.$emit('form-submitted', this.form)
        },
        creditworthinessUpdated () {
            this.form.creditworthiness_incident_type_id = null
        },
        updateSelectedElectronicInvoicingTypes (values) {
            this.form.electronic_invoicing_types = values
        },
        updateSelectedElectronicInvoicingOperators (values) {
            this.form.electronic_invoicing_operators = values
        },
        setElectronicInvoicingTypes (values) {
            return this.possibleElectronicInvoicingTypes.map((type) => ({
                ...type,
                selected: values !== null ? values.includes(type.value) : false,
            }))
        },
        updateSelectedPhpEolExcludedPaymentMethods (values) {
            this.form.php_eol_excluded_payment_methods = values
        },
        setAvailablePaymentMethodOptions (values) {
            return this.availablePaymentMethods.map((paymentMethod) => ({
                ...paymentMethod,
                selected: values !== null ? values.includes(paymentMethod.value) : false,
            }))
        },
    }
}
</script>

<style lang="scss" scoped>
.tenant-settings {
    display: grid;
    grid-gap: 25px;
}
</style>
